<template>
  <div class="absolute" :style="containerStyle" @click.stop @pointerdown.stop>
    <surface-editor-element-ui-container :element="element" resizable :on-drag-start="startDrag" />
    <textarea
      class="cui-surface-editor-text absolute bg-transparent outline-none focus:outline-none focus-visible:outline-none resize-none scrollbar-hide"
      :style="textAreaStyle"
      v-model="text"
      ref="textEl"
      spellcheck="false"
    />
  </div>
</template>

<script setup>

  import {storeToRefs} from 'pinia';

  const props = defineProps({
    element: Object,
    surface: Object,
    initiatingEvent: Object
  });

  const surfaceEditorStore = useSurfaceEditorStore();
  const {scale} = storeToRefs(surfaceEditorStore);
  const googleFonts = useGoogleFonts();
  const {startDrag, stopDrag} = useSurfaceEditorElementDrag();

  const content = computed(() => props.element.content);
  const textEl = ref();
  const originalText = content.value.text;

  const containerStyle = computed(() => surfaceEditorStore.getScaledElementPositionStyle({element: props.element}));
  const textAreaStyle = computed(() => {
    const {width, height} = surfaceEditorStore.getScaledElementPositionStyle({
      element: props.element,
      hAdjustment: ((content.value.line_height * content.value.font_size) / 4)
    });

    return {
      width,
      height,
      ...googleFonts.parseFontString(content.value.font_family).css,
      textAlign: content.value.align,
      fontSize: `${content.value.font_size * scale.value * content.value.scale}px`,
      lineHeight: `${content.value.line_height * content.value.font_size * scale.value * content.value.scale}px`,
      letterSpacing: `${content.value.letter_spacing || 0}em`,
      fontWeight: content.value.bold ? 600 : 'normal',
      fontStyle: content.value.italic ? 'italic' : 'normal',
      textDecoration: (content.value.underline || content.value.strikethrough)
        ? `${content.value.underline ? 'underline' : ''} ${content.value.strikethrough ? 'line-through' : ''}`
        : 'none',
      color: content.value.color
    };
  });

  const text = computed({
    get() {
      return content.value.text;
    },
    set(text) {
      surfaceEditorStore.updateElement({
        element: props.element,
        updates: {
          content: {text}
        },
        commit: false
      });
    }
  });

  onMounted(async () => {
    surfaceEditorStore.resetTextToScale({element: props.element});

    if (props.initiatingEvent) {
      textEl.value.focus();
      textEl.value.setSelectionRange(0, text.value.length);
    }
  });

  onUnmounted(() => {
    stopDrag();

    if (originalText !== text.value) {
      if (text.value) {
        surfaceEditorStore.updateElement({
          element: props.element,
          updates: {
            content: {
              text: text.value
            }
          }
        });
      } else {
        surfaceEditorStore.deleteElement({
          element: props.element,
          surface: props.surface
        });
      }

    }

    surfaceEditorStore.resetTextToScale({element: props.element});
  });

</script>
